import type { QueryClient } from '@tanstack/react-query';
import { meetingKeys, statusKeys } from './query-keys';

/**
 * The per-audit views that EMBED document fields: the status board (a step's attached
 * documents), every open meeting detail (its files) and the meeting picker. A replace or a
 * rename changes a document they show, and a swap deletes the source a step or meeting may
 * still hold, so after it they must re-read or the next attach re-sends a dead id. Shared by
 * the actor's `useReplaceDocument.onSettled`, the `document` update/delete echo and the
 * reconnect pass in `useSyncAuditData`, so the set cannot drift between them
 * (docs/DATA_FLOW.md §4).
 *
 * `refetchType: 'active'`: only views on screen re-read. An echo or the reconnect pass JOINS a
 * refetch already in flight (`cancelRefetch: false`): one swap arrives as an update echo and a
 * delete echo, both after the commit. The actor's settled replace passes `restartInFlight`: a
 * read in flight then may have begun before the commit, so it is restarted rather than trusted.
 * The board's query is disabled while it is in edit mode, so this only marks it there; leaving
 * edit mode re-reads it (`Status/index.tsx`).
 */
export const invalidateDocumentEmbeddingViews = (
  queryClient: QueryClient,
  auditId: string,
  { restartInFlight = false }: { restartInFlight?: boolean } = {},
): void => {
  const inFlight = { cancelRefetch: restartInFlight };
  void queryClient.invalidateQueries(
    { queryKey: statusKeys.all(auditId), refetchType: 'active' },
    inFlight,
  );
  void queryClient.invalidateQueries(
    { queryKey: meetingKeys.details(auditId), refetchType: 'active' },
    inFlight,
  );
  void queryClient.invalidateQueries(
    { queryKey: meetingKeys.allForAudit(auditId), refetchType: 'active' },
    inFlight,
  );
};
